'use client';
import { useRef, useEffect, useState } from 'react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ImageProps, ToutContentProps } from '@repo/ui/src/types';
import { useIsMD, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { useIntersectionObserver } from '@repo/ui/src/hooks/useIntersectionObserver';

export interface CardProps {
  backgroundImage: ImageProps;
  toutContent: ToutContentProps;
}

const CalloutSliderCard = ({ toutContent, backgroundImage }: CardProps) => {
  const backgroundImageUrl = buildImageSrc(backgroundImage, 800, 1, 1);
  const ref = useRef<HTMLDivElement | null>(null);
  const isMed = useIsMD();
  const reduceMotion = useReducedMotion();
  const [translateHeight, setHeight] = useState(0);
  const [visible, setVisible] = useState(false);
  const intersection = useIntersectionObserver(ref, { threshold: 0.8 });

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const bottomContent = element.querySelector('.bottom-content');
      const bottomContentHeight = bottomContent?.clientHeight;
      if (bottomContentHeight) {
        setHeight(bottomContentHeight);
      }
    }
  }, []);

  useEffect(() => {
    if (intersection?.isIntersecting) {
      setVisible(true);
    }
  }, [intersection]);

  return (
    <div
      ref={ref}
      className="callout-slider-item tw-relative tw-h-[85vh] tw-max-h-[800px] tw-flex tw-flex-col tw-overflow-hidden tw-min-h-[500px] tw-mb-6 focus-visible:tw-outline-4 focus-visible:tw-outline-brand-500 md:tw-w-[33.33%] md:tw-px-[.8vw] md:tw-mb-0 md:hover:tw-w-[85vh] md:hover:tw-flex-shrink-0 md:hover:motion-reduce:tw-w-[33.33%] tw-group/card tw-transition-all tw-duration-700 tw-ease-transitionQuad"
      tabIndex={0}
      data-testid="callout-slider-card"
    >
      <LazyLoadImage
        className="tw-overflow-hidden tw-h-full"
        imgComponent={
          <img
            src={backgroundImageUrl}
            role="presentation"
            alt=""
            className="tw-w-full tw-h-full tw-object-cover tw-object-left-center tw-overflow-hidden"
          />
        }
      />
      <div className="tw-bg-calloutMobile md:tw-bg-calloutMedium lg:tw-bg-calloutLarge tw-absolute tw-z-10 tw-top-0 tw-bottom-0 tw-left-0 md:tw-left-[.8vw] tw-right-0 md:tw-right-[.8vw]">
        <ToutContent
          dark
          divider
          narrowContent
          align="left"
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          {...toutContent}
          headingStyleVariant="h2"
          style={{
            transform: !reduceMotion && isMed && `translateY(${translateHeight + 70}px)`,
          }}
          className={classNames(
            'callout-slider-content tw-absolute tw-z-10 tw-top-0 tw-left-0 tw-bottom-0 tw-flex tw-flex-col tw-justify-end',
            'tw-right-[0.5px] tw-p-6 md:tw-pb-[3.5rem] md:tw-mx-[0.8vw] lg:tw-p-[3.5rem] tw-transition-all tw-duration-700',
            'sm:[&_.heading]:tw-pb-12',
            isMed
              ? `group-hover:tw-duration-700 tw-ease-in-out group-hover:tw-opacity-0 content
                group-hover/card:[&.content]:tw-opacity-100 group-hover/card:[&_.bottom-content]:tw-opacity-100
                group-hover/card:[&_.bottom-content]:tw-duration-700 [&_.bottom-content]:tw-transition-opacity
                group-hover/card:[&_.bottom-content]:tw-ease-in group-focus-visible/card:tw-opacity-100 group-hover/card:!tw-translate-y-0`
              : visible
                ? 'tw-opacity-100'
                : 'tw-opacity-0',
          )}
        />
      </div>
    </div>
  );
};

export default CalloutSliderCard;
